import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import {
  EquipmentAsset,
  EquipmentCheckoutInput,
  EquipmentReserveInput,
  ReservationStatus,
} from '@models/warehouse.model';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import ConstructionIcon from '@mui/icons-material/Construction';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useProjects } from '@hooks/api/projects';
import { useTasks } from '@hooks/api/task/useTasks';
import { useEquipmentReservations } from '@hooks/api/warehouse';
import { formatDateOnly, formatDateTime } from '@utils/formatDate';

interface EquipmentCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckout: (input: EquipmentCheckoutInput) => Promise<void>;
  onReserve?: (input: EquipmentReserveInput) => Promise<void>;
  equipment: EquipmentAsset | null;
  isLoading?: boolean;
  mode?: 'checkout' | 'reserve';
}

const toDateString = (date: string | Date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

const toTimeString = (date: string | Date) => {
  const d = new Date(date);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const EquipmentCheckoutModal: React.FC<EquipmentCheckoutModalProps> = ({
  isOpen,
  onClose,
  onCheckout,
  onReserve,
  equipment,
  isLoading = false,
  mode = 'checkout',
}) => {
  const isReserve = mode === 'reserve';

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('23:59');
  const [notes, setNotes] = useState('');

  // Fetch projects and tasks
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { tasks, loading: tasksLoading } = useTasks(selectedProjectId || undefined);

  // Fetch reservations for this asset (for conflict detection and auto-suggest)
  const { data: assetReservations = [] } = useEquipmentReservations(
    equipment?.assetId || undefined,
  );

  // Find matching reservation for selected task (auto-suggest)
  const matchingReservation = useMemo(() => {
    if (!taskId || isReserve) return null;
    return assetReservations.find(
      (r) =>
        r.taskId === taskId &&
        r.status === ReservationStatus.ACTIVE &&
        !r.isCheckoutReservation,
    ) || null;
  }, [assetReservations, taskId, isReserve]);

  // Detect conflicting reservations from other tasks
  const conflictingReservations = useMemo(() => {
    if (!taskId || !startDate || !endDate) return [];
    const userStart = new Date(`${startDate}T${startTime || '00:00'}:00`);
    const userEnd = new Date(`${endDate}T${endTime || '23:59'}:00`);

    return assetReservations.filter((r) => {
      if (r.taskId === taskId) return false;
      if (r.status !== ReservationStatus.ACTIVE) return false;
      if (r.isCheckoutReservation) return false;
      const rStart = new Date(r.startDate);
      const rEnd = new Date(r.endDate);
      return rStart <= userEnd && rEnd >= userStart;
    });
  }, [assetReservations, taskId, startDate, startTime, endDate, endTime]);

  const hasConflicts = conflictingReservations.length > 0;

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setSelectedProjectId('');
      setTaskId('');
      setStartDate('');
      setStartTime('00:00');
      setEndDate('');
      setEndTime('23:59');
      setNotes('');
    }
  }, [isOpen]);

  // Reset task when project changes
  useEffect(() => {
    setTaskId('');
  }, [selectedProjectId]);

  // Auto-fill dates from matching reservation or task schedule
  useEffect(() => {
    if (!taskId) return;

    if (!isReserve && matchingReservation) {
      // Auto-fill from existing reservation
      setStartDate(toDateString(matchingReservation.startDate));
      setStartTime(toTimeString(matchingReservation.startDate));
      setEndDate(toDateString(matchingReservation.endDate));
      setEndTime(toTimeString(matchingReservation.endDate));
    } else if (!isReserve) {
      // Fallback: auto-fill from task dates
      const selectedTask = tasks.find((t) => t.taskId === taskId);
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      setStartTime(toTimeString(new Date()));
      if (selectedTask?.endTime) {
        setEndDate(toDateString(selectedTask.endTime));
        setEndTime('23:59');
      }
    }
  }, [taskId, tasks, isReserve, matchingReservation]);

  const handleSubmit = async () => {
    if (!equipment || !taskId) return;

    const combinedStartDate = startDate
      ? new Date(`${startDate}T${startTime || '00:00'}:00`).toISOString()
      : undefined;
    const combinedEndDate = endDate
      ? new Date(`${endDate}T${endTime || '23:59'}:00`).toISOString()
      : undefined;

    if (isReserve && onReserve) {
      await onReserve({
        assetId: equipment.assetId,
        taskId,
        startDate: combinedStartDate,
        endDate: combinedEndDate,
        notes: notes.trim() || undefined,
      });
    } else {
      await onCheckout({
        assetId: equipment.assetId,
        taskId,
        startDate: combinedStartDate,
        endDate: combinedEndDate,
        notes: notes.trim() || undefined,
      });
    }
  };

  const title = isReserve ? 'Reserve Equipment' : 'Checkout Equipment';
  const confirmText = isReserve ? 'Reserve Equipment' : 'Checkout';

  const projectOptions: SelectOption[] = projects.map((p) => ({
    value: p.projectId || '',
    label: p.name,
  }));

  const taskOptions: SelectOption[] = tasks
    .filter((t) => t.taskId)
    .map((t) => ({
      value: t.taskId!,
      label: t.name,
    }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      confirmText={confirmText}
      handleConfirm={handleSubmit}
      isLoading={isLoading}
      disableConfirm={!taskId || hasConflicts}
      size="medium"
    >
      <div className="flex flex-col gap-5">
        {/* Equipment Info */}
        {equipment && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <ConstructionIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{equipment.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {equipment.serialNumber || equipment.assetCode || ''}
                  {equipment.manufacturer ? ` - ${equipment.manufacturer}` : ''}
                  {equipment.model ? ` ${equipment.model}` : ''}
                </p>
              </div>
            </div>

            {/* Cost Rates */}
            {(equipment.hourlyRate || equipment.clientHourlyRate) && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center gap-1.5 mb-2">
                  <AttachMoneyIcon className="text-gray-400" sx={{ fontSize: 14 }} />
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Rates</p>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  {equipment.hourlyRate != null && equipment.hourlyRate > 0 && (
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Cost / hour</span>
                      <span className="text-xs font-medium text-gray-900">${equipment.hourlyRate.toFixed(2)}</span>
                    </div>
                  )}
                  {equipment.clientHourlyRate != null && equipment.clientHourlyRate > 0 && (
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Client / hour</span>
                      <span className="text-xs font-medium text-green-700">${equipment.clientHourlyRate.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Project Selection */}
        <FormGroup label="Project *" htmlFor="checkoutProject">
          <ReactSelect
            options={projectOptions}
            value={
              selectedProjectId
                ? projectOptions.find((o) => o.value === selectedProjectId) || null
                : null
            }
            onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
              if (option && !Array.isArray(option) && 'value' in option) {
                setSelectedProjectId(option.value as string);
              } else {
                setSelectedProjectId('');
              }
            }}
            placeholder="Select a project..."
            isSearchable
            isClearable
            isLoading={projectsLoading}
          />
        </FormGroup>

        {/* Task Selection — only visible after project is selected */}
        {selectedProjectId && (
          <FormGroup label="Task *" htmlFor="checkoutTask">
            <ReactSelect
              options={taskOptions}
              value={
                taskId
                  ? taskOptions.find((o) => o.value === taskId) || null
                  : null
              }
              onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                if (option && !Array.isArray(option) && 'value' in option) {
                  setTaskId(option.value as string);
                } else {
                  setTaskId('');
                }
              }}
              placeholder={tasksLoading ? 'Loading tasks...' : 'Select a task...'}
              isSearchable
              isClearable
              isLoading={tasksLoading}
              isDisabled={tasksLoading}
            />
            {!tasksLoading && tasks.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">No tasks found for this project.</p>
            )}
          </FormGroup>
        )}

        {/* Task Date Reference */}
        {taskId && (() => {
          const selectedTask = tasks.find((t) => t.taskId === taskId);
          if (!selectedTask || (!selectedTask.startTime && !selectedTask.endTime)) return null;
          return (
            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
              <div className="flex items-center gap-2 mb-1.5">
                <CalendarTodayIcon className="text-blue-500" sx={{ fontSize: 14 }} />
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Task Schedule</p>
              </div>
              <div className="flex gap-6">
                {selectedTask.startTime && (
                  <div>
                    <p className="text-xs text-blue-400">Start</p>
                    <p className="text-sm font-medium text-gray-900">{formatDateOnly(selectedTask.startTime)}</p>
                  </div>
                )}
                {selectedTask.endTime && (
                  <div>
                    <p className="text-xs text-blue-400">End</p>
                    <p className="text-sm font-medium text-gray-900">{formatDateOnly(selectedTask.endTime)}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Auto-suggest banner: reservation found for this task */}
        {!isReserve && matchingReservation && (
          <div className="p-3 bg-green-50 rounded-md border border-green-200">
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="text-green-500 mt-0.5" sx={{ fontSize: 16 }} />
              <div>
                <p className="text-sm font-medium text-green-800">Reservation found for this task</p>
                <p className="text-xs text-green-700 mt-0.5">
                  {formatDateTime(matchingReservation.startDate)} — {formatDateTime(matchingReservation.endDate)}. Dates have been pre-filled.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Conflict Warning */}
        {hasConflicts && (
          <div className="p-3 bg-red-50 rounded-md border border-red-200">
            <div className="flex items-start gap-2">
              <WarningAmberIcon className="text-red-500 mt-0.5" sx={{ fontSize: 16 }} />
              <div>
                <p className="text-sm font-medium text-red-800">Checkout blocked — date conflict</p>
                <p className="text-xs text-red-700 mt-0.5">
                  This equipment is reserved for other tasks during the selected period:
                </p>
                <ul className="mt-1 space-y-0.5">
                  {conflictingReservations.map((r) => (
                    <li key={r.reservationId} className="text-xs text-red-600">
                      <span className="font-medium">{r.task?.name || 'Unknown task'}</span>
                      {r.task?.project && <span className="text-red-500"> ({r.task.project.name})</span>}
                      {' — '}
                      {formatDateTime(r.startDate)} to {formatDateTime(r.endDate)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Date/Time Fields — unified for both modes */}
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="From Date" htmlFor="startDate">
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="From Time" htmlFor="startTime">
            <Input
              id="startTime"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Until Date" htmlFor="endDate">
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Until Time" htmlFor="endTime">
            <Input
              id="endTime"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </FormGroup>
        </div>

        {/* Notes */}
        <FormGroup label="Notes" htmlFor="checkoutNotes">
          <textarea
            id="checkoutNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            rows={3}
          />
        </FormGroup>

        {/* Reserve Info Banner */}
        {isReserve && (
          <div className="p-3 bg-purple-50 rounded-md border border-purple-200">
            <div className="flex items-start gap-2">
              <InfoOutlinedIcon className="text-purple-500 mt-0.5" sx={{ fontSize: 16 }} />
              <p className="text-sm text-purple-700">
                Reserving equipment will block it from being checked out by other tasks during the specified date and time range.
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EquipmentCheckoutModal;
